import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import {
  ChartBar,
  CalendarBlank,
  Users,
  Ticket,
  ArrowsClockwise,
  UserCircle,
  CashRegister,
  CreditCard,
  Barbell,
  Calendar,
  X,
  Student,
  Confetti,
  Trophy,
  UserPlus,
  Shield,
} from '@phosphor-icons/react';

const Sidebar = ({ mobileOpen, onClose }) => {
  const { user, t, language } = useAuth();
  const location = useLocation();
  
  const isAdmin = user?.role === 'admin';
  const isTrainer = user?.role === 'trainer';
  const L = (en, de) => language === 'de' ? de : en;

  const adminMenuItems = [
    { path: '/admin', label: t('dashboard'), icon: ChartBar, roles: ['admin'] },
    { path: '/admin/work-plan', label: L('Work plan', 'Arbeitsplan'), icon: Calendar, roles: ['admin', 'staff'] },
    { path: '/admin/bookings', label: t('bookings'), icon: CalendarBlank, roles: ['admin', 'staff'] },
    { path: '/admin/cash-register', label: L('Cash Register', 'Kasse'), icon: CashRegister, roles: ['admin', 'staff'] },
    { path: '/admin/users', label: t('users'), icon: Users, roles: ['admin', 'staff'] },
    { path: '/admin/vouchers', label: t('vouchers'), icon: Ticket, roles: ['admin', 'staff'] },
    { path: '/admin/payments', label: L('Payments', 'Zahlungen'), icon: CreditCard, roles: ['admin', 'staff'] },
    { path: '/admin/trainers', label: L('Trainers', 'Trainer'), icon: Barbell, roles: ['admin', 'staff'] },
    { path: '/admin/training-programs', label: L('Group Training', 'Gruppen-Training'), icon: Student, roles: ['admin', 'staff'] },
    { path: '/admin/academy', label: L('Academy', 'Akademie'), icon: Student, roles: ['admin'] },
    { path: '/trainer/schedule', label: L('Trainer Schedule', 'Trainerplan'), icon: Calendar, roles: ['admin', 'staff'] },
    { path: '/community', label: L('Community', 'Community'), icon: Confetti, roles: ['admin', 'staff'] },
    { path: '/admin/member-of-the-month', label: L('Member of the Month', 'Member des Monats'), icon: Trophy, roles: ['admin', 'staff'] },
    { path: '/admin/migration', label: L('Migration', 'Migration'), icon: UserPlus, roles: ['admin'] },
    { path: '/admin/audit', label: L('Audit Log', 'Audit-Log'), icon: Shield, roles: ['admin'] },
  ];

  const trainerMenuItems = [
    { path: '/trainer/schedule', label: L('Schedule', 'Wochenplan'), icon: Calendar },
    { path: '/admin/users', label: t('users'), icon: Users },
    { path: '/book', label: t('book'), icon: CalendarBlank },
    { path: '/admin/training-programs', label: L('Group Training', 'Gruppen-Training'), icon: Student },
    ...(user?.manages_community ? [{ path: '/community', label: L('Community', 'Community'), icon: Confetti }] : []),
    ...(user?.manages_academy ? [{ path: '/admin/academy', label: L('Academy', 'Akademie'), icon: Student }] : []),
    { path: '/profile', label: t('profile'), icon: UserCircle },
  ];

  const customerLinks = [
    { path: '/book', label: t('book'), icon: CalendarBlank },
    { path: '/matches', label: t('matches'), icon: Users },
    { path: '/trainings', label: L('Trainings & Classes', 'Trainings & Kurse'), icon: Student },
    { path: '/community', label: L('Community', 'Community'), icon: Confetti },
    { path: '/leaderboard', label: L('Leaderboard', 'Bestenliste'), icon: Trophy },
    { path: '/profile', label: t('profile'), icon: UserCircle },
  ];

  const handleLinkClick = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside 
        className={`fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-slate-200 z-50 transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Mobile close button */}
          <div className="lg:hidden flex justify-end p-3">
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          </div>

          <nav className="flex-1 py-4 lg:py-6 px-4 space-y-1 overflow-y-auto">
            {/* Admin/Staff Menu */}
            {!isTrainer && (
              <>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-4">
                  {isAdmin ? 'Admin Menu' : 'Staff Menu'}
                </p>
                {adminMenuItems
                  .filter(item => item.roles.includes(user?.role))
                  .map(({ path, label, icon: Icon }) => {
                    const isActive = location.pathname === path;
                    return (
                      <Link key={path} to={path} onClick={handleLinkClick}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${isActive ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
                        data-testid={`sidebar-${path.split('/').pop()}`}>
                        <Icon size={20} weight={isActive ? 'fill' : 'duotone'} className={isActive ? 'text-emerald-600' : ''} />
                        <span className="text-sm">{label}</span>
                      </Link>
                    );
                  })}
                <div className="border-t border-slate-100 my-6" />
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-4">Customer View</p>
                {customerLinks.map(({ path, label, icon: Icon }) => {
                  const isActive = location.pathname === path;
                  return (
                    <Link key={path} to={path} onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${isActive ? 'text-emerald-600' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-50'}`}
                      data-testid={`sidebar-customer-${path.slice(1)}`}>
                      <Icon size={18} weight="duotone" />
                      <span className="text-sm">{label}</span>
                    </Link>
                  );
                })}
              </>
            )}

            {/* Trainer Menu */}
            {isTrainer && (
              <>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-4">
                  {L('Trainer Menu', 'Trainer Menu')}
                </p>
                {trainerMenuItems.map(({ path, label, icon: Icon }) => {
                  const isActive = location.pathname === path;
                  return (
                    <Link key={path} to={path} onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${isActive ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
                      data-testid={`sidebar-${path.split('/').pop()}`}>
                      <Icon size={20} weight={isActive ? 'fill' : 'duotone'} className={isActive ? 'text-emerald-600' : ''} />
                      <span className="text-sm">{label}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
